// Blokkauspatternit. Kaksi fallback-listaa, jotka poikkeavat toisistaan:
// inline-blokkerin lista on suppeampi kuin pääskriptin. Ero on peritty
// vaihe 0:n toteutuksesta ja säilytetään V1:ssä sellaisenaan (käyttäytymisen
// on säilyttävä identtisenä) — yhtenäistäminen on vaihe 2:n asia.

import type { BlockMatch, BlockPatterns, Choices } from './types';
import { patternsKey } from './storage';

/** Inline-blokkerin fallback-patternit (edge-funktion part=blocker). */
export const BLOCKER_FALLBACK_PATTERNS: BlockPatterns = {
  analytics: ['google-analytics.com', 'googletagmanager.com', 'analytics.google.com', 'hotjar.com', 'clarity.ms'],
  marketing: ['connect.facebook.net', 'doubleclick.net', 'googlesyndication.com', 'googleadservices.com', 'fbevents.js'],
  social: ['platform.twitter.com', 'platform.linkedin.com'],
  preferences: ['intercomcdn.com', 'crisp.chat'],
};

/** Pääskriptin fallback-patternit (käytössä vain jos inline-blokkeria ei ole). */
export const MAIN_FALLBACK_PATTERNS: BlockPatterns = {
  analytics: ['google-analytics.com', 'googletagmanager.com', 'analytics.google.com', 'plausible.io', 'hotjar.com', 'clarity.ms'],
  marketing: ['connect.facebook.net', 'ads.linkedin.com', 'snap.licdn.com', 'doubleclick.net', 'googlesyndication.com', 'googleadservices.com', 'fbevents.js'],
  social: ['platform.twitter.com', 'platform.linkedin.com'],
  preferences: ['intercomcdn.com', 'crisp.chat'],
};

/** Kopioi patternit uudeksi objektiksi (taulukot slice-kopioina). */
export function clonePatterns(source: BlockPatterns): BlockPatterns {
  const target: BlockPatterns = {};
  for (const k in source) target[k] = source[k].slice();
  return target;
}

/** Yhdistää localStorage-välimuistin patternit (inline-blokkerin merge). */
export function mergeCachedPatterns(target: BlockPatterns, cached: BlockPatterns | null | undefined): void {
  if (!cached) return;
  for (const s in cached) {
    if (!target[s]) target[s] = [];
    for (let j = 0; j < cached[s].length; j++) {
      if (target[s].indexOf(cached[s][j]) === -1) target[s].push(cached[s][j]);
    }
  }
}

/** Lukee patternivälimuistin localStoragesta (tai null). */
export function readCachedPatterns(domain: string): BlockPatterns | null {
  try {
    return JSON.parse(localStorage.getItem(patternsKey(domain)) as string);
  } catch {
    return null;
  }
}

/**
 * Pitääkö src estää annetuilla valinnoilla. Välttämättömät kategoriat
 * ('essential', 'valttamattomat') ohitetaan aina; hyväksytyt kategoriat
 * ohitetaan; osuma on case-insensitiivinen substring-vertailu.
 */
export function shouldBlockSrc(
  patterns: BlockPatterns,
  src: string | null | undefined,
  consent: Choices | null | undefined,
): BlockMatch | false {
  if (!src) return false;
  const s = src.toLowerCase();
  for (const slug in patterns) {
    if (slug === 'essential' || slug === 'valttamattomat') continue;
    if (consent && consent[slug]) continue;
    const list = patterns[slug];
    for (let i = 0; i < list.length; i++) {
      if (s.indexOf(list[i].toLowerCase()) !== -1) return { slug, pattern: list[i] };
    }
  }
  return false;
}
